import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Layers, MapPin, ChevronRight, Tag } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AreaPickerDialog from "@/components/AreaPickerDialog";
import { mergeActivityTypes, activityTypeColor } from "@/lib/activityTypes";

const PLACEMENT_META = {
  suelo: { label: "Suelos", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pared: { label: "Paredes", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  techo: { label: "Techos", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  espacio: { label: "Espacios", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  organizacion: { label: "Organización", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};
const EMPTY = { name: "", description: "", area_id: "", preference: "", activity_type: "" };

export default function ActivityModal({ open, onOpenChange, areas, levels, levelName, units, activities, activity, defaultAreaId, onSaved, onAreaCreated, onAreaUpdated }) {
  const editing = Boolean(activity);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [placement, setPlacement] = useState("");

  useEffect(() => {
    if (!open) return;
    if (activity) setForm({ name: activity.name || "", description: activity.description || "", area_id: activity.area_id || "", preference: activity.preference || "", activity_type: activity.activity_type || "" });
    else setForm({ ...EMPTY, area_id: defaultAreaId || "" });
  }, [open, activity, defaultAreaId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const typeOptions = useMemo(() => mergeActivityTypes(activities), [activities]);
  const selectedArea = useMemo(() => (areas || []).find((a) => a.area_id === form.area_id) || null, [areas, form.area_id]);

  useEffect(() => { setPlacement(selectedArea?.placement || ""); }, [selectedArea]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre de la actividad.");
    if (!form.area_id) return toast.error("Selecciona un área de trabajo.");
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description.trim() || null, area_id: form.area_id, preference: form.preference.trim(), activity_type: form.activity_type.trim() };
    try {
      const r = editing
        ? await api.put(`/model/activities/${activity.activity_id}`, payload)
        : await api.post("/model/activities", payload);
      if (selectedArea && (placement || "") !== (selectedArea.placement || "")) {
        try {
          const ar = await api.put(`/model/work-areas/${selectedArea.area_id}`, { placement: placement || null });
          onAreaUpdated?.(ar.data);
        } catch { /* el espacio es opcional; no bloquea el guardado */ }
      }
      onSaved(r.data, editing);
      onOpenChange(false);
      toast.success(editing ? "Actividad actualizada." : "Actividad añadida.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la actividad.");
    } finally {
      setSaving(false);
    }
  };

  const pm = selectedArea ? PLACEMENT_META[selectedArea.placement] : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="activity-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar actividad" : "Nueva actividad"}</DialogTitle>
            <DialogDescription>Define qué hay que hacer y en qué área de trabajo (dónde).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-0.5">
            <div className="space-y-1.5">
              <Label htmlFor="act-name">Actividad (QUÉ) *</Label>
              <Input id="act-name" data-testid="activity-name-input" placeholder="Ej. Alicatado de cocina"
                value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="act-type" className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Tipo de actividad</Label>
              <Input id="act-type" data-testid="activity-type-input" placeholder="Ej. Organización, Instalaciones… (o escribe uno nuevo)"
                value={form.activity_type} onChange={(e) => set("activity_type", e.target.value)} list="activity-type-options" />
              <datalist id="activity-type-options">
                {typeOptions.map((t) => <option key={t} value={t} />)}
              </datalist>
              {typeOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {typeOptions.map((t) => {
                    const sel = form.activity_type.trim().toLowerCase() === t.toLowerCase();
                    return (
                      <button type="button" key={t} onClick={() => set("activity_type", sel ? "" : t)} data-testid={`activity-type-chip-${t}`}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${sel ? activityTypeColor(t) + " ring-1 ring-offset-1 ring-current/30" : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="act-pref">Preferencia (orden dentro del nivel)</Label>
              <Input id="act-pref" data-testid="activity-preference-input" placeholder="Ej. 1, A, 1.2…"
                value={form.preference} onChange={(e) => set("preference", e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="act-desc">Descripción</Label>
              <Textarea id="act-desc" data-testid="activity-description-input" rows={2}
                placeholder="Detalle de la actividad (opcional)" value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label>Área de trabajo (DÓNDE) *</Label>
              <button type="button" onClick={() => setPickerOpen(true)} data-testid="activity-area-picker-trigger"
                className="w-full flex items-center gap-3 rounded-lg border border-border bg-background px-3.5 py-3 text-left hover:border-primary/50 hover:bg-secondary/40 transition-colors">
                <MapPin className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  {selectedArea ? (
                    <>
                      <div className="text-sm font-medium truncate">{selectedArea.name}</div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                        <Layers className="h-3 w-3" /> {levelName[selectedArea.level_id] || "—"}
                        {pm && <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium ${pm.cls}`}>{pm.label}</span>}
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-muted-foreground">Pulsa para buscar y seleccionar un área…</span>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
              {selectedArea && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Espacio</Label>
                    <Select value={placement || "none"} onValueChange={(v) => setPlacement(v === "none" ? "" : v)}>
                      <SelectTrigger className="h-9" data-testid="activity-espacio-select"><SelectValue placeholder="Sin espacio" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin espacio</SelectItem>
                        {Object.entries(PLACEMENT_META).map(([k, v]) => (
                          <SelectItem key={k} value={k} data-testid={`activity-espacio-opt-${k}`}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Compartido con el Área de trabajo.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Uds relacionadas (del área)</Label>
                    <div className="h-9 flex items-center rounded-md border border-border bg-background-subtle px-3 text-sm font-mono" data-testid="activity-related-units-display">
                      {selectedArea.measure ? `${selectedArea.measure} ${selectedArea.unit || ""}`.trim() : "—"}
                    </div>
                    {!selectedArea.measure && <p className="text-[10px] text-muted-foreground">Defínelas en ¿DÓNDE? (Medida del área).</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="button" className="rounded-full" onClick={submit} disabled={saving} data-testid="activity-save-button">
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Añadir actividad"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AreaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        areas={areas || []}
        levels={levels || []}
        units={units || []}
        value={form.area_id}
        onSelect={(id) => set("area_id", id)}
        onAreaCreated={onAreaCreated}
      />
    </>
  );
}
